import Big from "big.js";
import type { TeamMemberVersion } from "./teamVersions";

interface TierPrice {
  /** 每人每月均价（展示用，产品测算固定值） */
  monthlyFee: number;
  /** 每人每年折后价（计费用，产品测算固定值） */
  yearlyFee: number;
}

export interface PricingTier {
  /** 档位名（展示/调试用） */
  label: string;
  /** 命中上限：totalSeatCount <= maxCount 即命中（按数组顺序取第一个满足项） */
  maxCount: number;
  /** 1 年期折扣（当前仅售 1 年） */
  discount: number;
  prices: Record<TeamMemberVersion, TierPrice>;
}

/** 版本原价（元/人/年），用于划线价与「已优惠」计算 */
export const basePrice: Record<TeamMemberVersion, number> = {
  pro: 1188,
  plus: 2388,
  ultra: 5139
};

/**
 * 团队版三档人数定价（2026-07 商业化调优后，仅 1 年期）
 * NOTE: 折后价/月均价为产品测算后的固定值，不在前端按折扣重算，
 * 避免四舍五入口径与后端、需求文档不一致
 */
export const teamPricingConfig: PricingTier[] = [
  {
    label: "2-4人",
    maxCount: 4,
    discount: 0.7,
    prices: {
      pro: { monthlyFee: 69.3, yearlyFee: 832 },
      plus: { monthlyFee: 139.3, yearlyFee: 1672 },
      ultra: { monthlyFee: 299.8, yearlyFee: 3597 }
    }
  },
  {
    label: "5-9人",
    maxCount: 9,
    discount: 0.68,
    prices: {
      pro: { monthlyFee: 67.3, yearlyFee: 808 },
      plus: { monthlyFee: 135.3, yearlyFee: 1624 },
      ultra: { monthlyFee: 291.3, yearlyFee: 3495 }
    }
  },
  {
    label: "10人及以上",
    maxCount: Infinity,
    discount: 0.65,
    prices: {
      pro: { monthlyFee: 64.3, yearlyFee: 772 },
      plus: { monthlyFee: 129.4, yearlyFee: 1552 },
      ultra: { monthlyFee: 278.4, yearlyFee: 3340 }
    }
  }
];

/** 微信支付单笔上限（元），超出后仅保留支付宝 */
export const WECHAT_MAX_AMOUNT = 6000;

/** 团队版一年按 372 天计（与后端计费口径一致）：开通到期日、增购补差价均以此折算 */
export const TEAM_YEAR_DAYS = 372;

/** 按团队总席位数解析价格档位；<2 人兜底最低档（表单最小值为 2，此处仅防御） */
export const resolveTier = (memberCount: number): PricingTier => {
  return teamPricingConfig.find((t) => memberCount <= t.maxCount) ?? teamPricingConfig[teamPricingConfig.length - 1];
};

/**
 * 获取团队版定价信息（仅 1 年期）
 * @param memberCount 团队总席位数（新购=购买人数；增购=已有+新增，档位按总数命中）
 * @param version 会员版本 pro/plus/ultra
 */
export const getPricing = (memberCount: number, version: TeamMemberVersion) => {
  const tier = resolveTier(memberCount);
  const fee = tier.prices[version];
  if (!fee) {
    return { basePrice: 0, discount: 0, monthlyFee: 0, yearlyFee: 0 };
  }
  return {
    basePrice: basePrice[version],
    discount: tier.discount,
    monthlyFee: fee.monthlyFee,
    yearlyFee: fee.yearlyFee
  };
};

/**
 * 折扣数字展示：0.7 → "7"，0.68 → "6.8"，0.65 → "6.5"
 * 业务规则：两位数折扣必须带小数点表达（65 折应显示为 6.5 折）；用 Big 规避 0.68×10 的浮点误差
 */
export const formatTeamDiscount = (discount: number): string => {
  return new Big(discount).times(10).toString();
};

/**
 * 由折后价 / 划线价反推折扣展示（如 832/1188 → "7"，808/1188 → "6.8"）
 * 保留 1 位小数，整数不带小数点
 */
export const formatPriceDiscount = (discountedPrice: number, originPrice: number): string => {
  if (!originPrice || !discountedPrice) {
    return "";
  }
  const ratioTimes10 = (discountedPrice / originPrice) * 10;
  const rounded = Math.round(ratioTimes10 * 10) / 10;
  return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1).replace(/\.0$/, "");
};
